import React from 'react';
import { CheckCircle, XCircle, CreditCard } from 'lucide-react';

const StkTestTab = ({ stkTest }) => {
  const { stkTestForm, setStkTestForm, stkTestLoading, stkTestResult, stkTestHistory, handleStkTest, testMpesaConnectivity } = stkTest;

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">M-Pesa STK Push Testing</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Test Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Send Test STK Push</h3>
          <p className="text-sm text-gray-600 mb-4">
            This will use the "Neverfull MM Tote Bags" product for testing purposes.
          </p>

          <form onSubmit={handleStkTest} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                value={stkTestForm.phoneNumber}
                onChange={(e) => setStkTestForm({ ...stkTestForm, phoneNumber: e.target.value })}
                placeholder="+254700000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d44100]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount (KSh)</label>
              <input
                type="number"
                value={stkTestForm.amount}
                onChange={(e) => setStkTestForm({ ...stkTestForm, amount: e.target.value })}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d44100]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
              <input
                type="text"
                value={stkTestForm.customerName}
                onChange={(e) => setStkTestForm({ ...stkTestForm, customerName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d44100]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer Email</label>
              <input
                type="email"
                value={stkTestForm.customerEmail}
                onChange={(e) => setStkTestForm({ ...stkTestForm, customerEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d44100]"
                required
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={stkTestLoading}
                className="flex-1 bg-[#d44100] text-white py-2 px-4 rounded-lg hover:bg-[#b83700] disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {stkTestLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send STK Push'
                )}
              </button>

              <button
                type="button"
                onClick={testMpesaConnectivity}
                className="px-4 py-2 border border-[#d44100] text-[#d44100] rounded-lg hover:bg-[#d44100] hover:text-white transition-colors"
              >
                Test Connectivity
              </button>
            </div>
          </form>
        </div>

        {/* Test Results */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Test Results</h3>

          {stkTestResult ? (
            <div className={`p-4 rounded-lg ${stkTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center mb-2">
                {stkTestResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mr-2" />
                )}
                <span className={`font-medium ${stkTestResult.success ? 'text-green-800' : 'text-red-800'}`}>
                  {stkTestResult.message}
                </span>
              </div>

              {stkTestResult.success && (
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Checkout Request ID:</span>
                    <span className="font-mono text-gray-800">{stkTestResult.checkoutRequestID}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order ID:</span>
                    <span className="font-mono text-gray-800">{stkTestResult.orderId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Formatted Phone:</span>
                    <span className="font-mono text-gray-800">{stkTestResult.formattedPhone}</span>
                  </div>
                  {stkTestResult.productUsed && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Product Used:</span>
                      <span className="text-gray-800">{stkTestResult.productUsed}</span>
                    </div>
                  )}
                  {stkTestResult.mpesaReceipt && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">M-Pesa Receipt:</span>
                      <span className="font-mono text-gray-800">{stkTestResult.mpesaReceipt}</span>
                    </div>
                  )}
                </div>
              )}

              {stkTestResult.error && (
                <div className="mt-3 p-2 bg-red-100 rounded text-sm text-red-800">
                  <strong>Error:</strong> {stkTestResult.error}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No test results yet. Send a test STK Push to see results here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Test History */}
      {stkTestHistory.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Tests</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Time</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Phone</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Amount</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Receipt</th>
                  <th className="text-left py-2 text-sm font-medium text-gray-600">Request ID</th>
                </tr>
              </thead>
              <tbody>
                {stkTestHistory.map((test) => (
                  <tr key={test.id} className="border-b border-gray-100">
                    <td className="py-2 text-sm text-gray-800">{test.timestamp}</td>
                    <td className="py-2 text-sm text-gray-800">{test.phoneNumber}</td>
                    <td className="py-2 text-sm text-gray-800">KSh {test.amount}</td>
                    <td className="py-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        test.status === 'completed' ? 'bg-green-100 text-green-800' :
                        test.status === 'timeout' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {test.status}
                      </span>
                    </td>
                    <td className="py-2 text-sm font-mono text-gray-600">{test.mpesaReceipt || '-'}</td>
                    <td className="py-2 text-sm font-mono text-gray-600">{test.checkoutRequestID}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StkTestTab;
